import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMemberWallet} from "../../../hooks/useMemberWallet";
import {BalanceArea} from "../../../components/bank/BalanceArea";
import {AccountInfo} from "../../../components/bank/AccountInfo";
import {inputSetComma, setComma, unComma, wonAndPercentFormat} from "../../../utils/format";
import {alertAfterFunction, defaultAlert, defaultConfirm} from "../../../utils/alert";
import {getWithdrawalFeeInfo, memberWithdrawalRequest} from "../../../api/transaction/withdrawalApi";
import {WithdrawalSettingType, WithdrawalTypes} from "../../../types/settlement/settlement.types";


export default function WithdrawalPage() {
    const navigate = useNavigate();
    const {memberWalletInfo, loading, error, refetch} = useMemberWallet();

    const [withdrawalSetting, setWithdrawalSettingInfo] = useState<WithdrawalSettingType>();

    const [totalWithdrawalAmt, setTotalWithdrawalAmt] = useState<number>(0);

    const [withdrawalType, setWithdrawalType] = useState<WithdrawalTypes>(
        {
            requestAmt: 0,
            withdrawalFee: 0
        }
    );


    const getWithdrawalFee = async () => {
        try {
            const response = await getWithdrawalFeeInfo();
            if (response.data?.code == "00") {
                setWithdrawalSettingInfo(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch WithdrawalPage:', err);
        }
    };

    const calcFee = (requestAmt: number): number => {
        if (requestAmt === 0) return 0;
        const standardAmt = 10000;
        if (requestAmt > standardAmt) {
            return withdrawalSetting?.withdrawFeeOverUnit === "WON"
                ? Number(withdrawalSetting?.withdrawFeeOverValue)
                : Math.floor((requestAmt * Number(withdrawalSetting?.withdrawFeeOverValue)) / 100);
        } else {
            return withdrawalSetting?.withdrawFeeUnderUnit === "WON"
                ? Number(withdrawalSetting?.withdrawFeeUnderValue)
                : Math.floor((requestAmt * Number(withdrawalSetting?.withdrawFeeUnderValue)) / 100);
        }
    };

    const inputRequestAmt = (requestAmt: number) => {
        const deposit = Number(memberWalletInfo?.withdrawableBalance);
        const fee = calcFee(requestAmt);
        const total = requestAmt + fee;

        if (requestAmt > 0 && deposit < total) {
            defaultAlert("출금 가능 금액을 초과할 수 없습니다.");
            requestAmt = 0;
        }

        const finalFee = requestAmt === 0 ? 0 : fee;
        const finalTotal = requestAmt === 0 ? 0 : total;

        setTotalWithdrawalAmt(finalTotal);
        setWithdrawalType(prev => ({
            ...prev,
            requestAmt: requestAmt,
            withdrawalFee: finalFee
        }));
    };

    // 잔액 전부 출금: balance = requestAmt + fee(requestAmt) 역산
    const handleMaxWithdrawal = () => {
        const balance = Number(memberWalletInfo?.withdrawableBalance);
        if (!balance) return;
        const standardAmt = 10000;

        // 1만원 초과 구간으로 역산 (ceil로 총 출금금액이 잔액과 일치하도록)
        let requestAmt: number;
        if (withdrawalSetting?.withdrawFeeOverUnit === "WON") {
            requestAmt = balance - Number(withdrawalSetting?.withdrawFeeOverValue);
        } else {
            const rate = Number(withdrawalSetting?.withdrawFeeOverValue);
            requestAmt = Math.ceil((balance * 100) / (100 + rate));
        }

        // 역산 결과가 1만원 이하면 이하 구간으로 재계산
        if (requestAmt <= standardAmt) {
            if (withdrawalSetting?.withdrawFeeUnderUnit === "WON") {
                requestAmt = balance - Number(withdrawalSetting?.withdrawFeeUnderValue);
            } else {
                const rate = Number(withdrawalSetting?.withdrawFeeUnderValue);
                requestAmt = Math.ceil((balance * 100) / (100 + rate));
            }
        }

        requestAmt = Math.max(0, requestAmt);

        // ceil로 인해 총액이 잔액 초과하면 1원 보정
        if (requestAmt + calcFee(requestAmt) > balance) {
            requestAmt -= 1;
        }

        inputRequestAmt(requestAmt);
    };


    const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
        inputSetComma(e);
    };

    const handleWithdrawal = () => {
        const withdrawMinAmount = Number(withdrawalSetting?.withdrawMinAmount);

        if (withdrawalType.requestAmt < withdrawMinAmount) {
            defaultAlert("최소 출금 금액은 " + setComma(withdrawMinAmount) + "원 입니다.");
            return;
        }

        defaultConfirm("출금을 신청하시겠습니까?", "출금 신청", "닫기", (isConfirmed) => {
            if (isConfirmed) {
                withdrawal();
            } else {
                return;
            }
        });
    }

    const withdrawal = async () => {
        try {
            const response = await memberWithdrawalRequest(withdrawalType);
            if (response.data?.code === "00") {
                alertAfterFunction("출금 신청이 정상적으로\n신청되었습니다.", () => navigate('/settlement/withdrawal-list'));
            } else {
                defaultAlert("출금 신청에 실패했습니다.");
                return;
            }
        } catch (err) {
            console.error('Failed to withdrawal:', err);
            defaultAlert("출금 신청에 실패했습니다.");
            return;
        }
    }


    useEffect(() => {
        getWithdrawalFee();
    }, []);

    return (
        <>
            {withdrawalSetting && (() => {
                return (
                    <>
                        {totalWithdrawalAmt > 0 &&
                            <div className="bottom-btn">
                                <button type="button"
                                        className="btn confirm"
                                        onClick={handleWithdrawal}
                                >출금신청하기
                                </button>
                            </div>
                        }
                        <main className="container type_gray">
                            <section className="bg-white">
                                <div className="inner-box">
                                    <BalanceArea
                                        className={"main-wallet settlement"}
                                        title={"출금 가능 금액"}
                                        balance={Number(memberWalletInfo?.withdrawableBalance || 0)}
                                        expectedBalance={0}
                                        expectedBalanceShow={false}
                                    />
                                    <ul className="guidF-list mt10">
                                        <li>정산 잔액금은 서비스가 완료되어 정산이 완료된 금액입니다.</li>
                                        <li>정산 예정 금액은 출금이 불가합니다.</li>
                                    </ul>
                                </div>
                            </section>
                            <section className="bg-white">
                                <fieldset>
                                    <legend>출금 요청 금액</legend>
                                    <div className="inner-box">
                                        <div className="form-item">
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '6px'
                                            }}>
                                                <label htmlFor="phone" className="input-tit big"
                                                       style={{marginBottom: 0}}>출금 요청 금액</label>
                                                <button type="button" className="all-btn"
                                                        onClick={handleMaxWithdrawal}>전체 출금
                                                </button>
                                            </div>
                                            <div className="inputboxWrap focus">
                                                <input type="text" className="inputbox" placeholder="0"
                                                       inputMode="numeric"  // 숫자 키패드
                                                       pattern="[0-9]*"
                                                       onInput={handleInput}
                                                       onChange={(e) => inputRequestAmt(unComma(e.currentTarget.value))}
                                                       value={setComma(withdrawalType.requestAmt)}
                                                />
                                                <label className="unit">원</label>
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <div className="info-row">
                                                <span className="label">출금금액</span>
                                                <span
                                                    className="cont">{setComma(withdrawalType.requestAmt || 0)}원</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="label">출금 수수료</span>
                                                <span
                                                    className="cont">{setComma(withdrawalType.withdrawalFee || 0)}원</span>
                                            </div>
                                            <div className="info-row top-line">
                                                <span className="label"><strong>총 출금 금액</strong></span>
                                                <span
                                                    className="cont"><strong>{setComma(totalWithdrawalAmt || 0)}원</strong></span>
                                            </div>
                                        </div>
                                        <div className="form-item">
                                            <div className="guide-box mt20">
                                                <h3 className="point-tit">출금 서비스 안내</h3>
                                                <ul className="guidF-list">
                                                    <li>1회 최소 출금 금액
                                                        : {setComma(withdrawalSetting.withdrawMinAmount || 0)}원
                                                    </li>
                                                    <li>출금 수수료
                                                        <br/>- 1만원 이하
                                                        :{setComma(withdrawalSetting.withdrawFeeUnderValue || 0)}
                                                        {wonAndPercentFormat(withdrawalSetting.withdrawFeeUnderUnit)}
                                                        <br/>-
                                                        1만원 초과 : 출금요청금액 {withdrawalSetting.withdrawFeeOverValue || 0}
                                                        {wonAndPercentFormat(withdrawalSetting.withdrawFeeOverUnit)}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </section>

                            <AccountInfo
                                title={"출금계좌"}
                                depositor={memberWalletInfo?.memberName}
                                accountNo={memberWalletInfo?.accountNo}
                                bankName={memberWalletInfo?.bankName}
                            />

                            <section className="no-gap">
                                <div className="inner-box">
                                    <ul className="guidF-list">
                                        <li>출금 신청 전 계좌번호와 예금주명을 반드시 확인해 주세요.</li>
                                        <li>출금 신청 시 3영업일 이내 출금계좌로 지급될 예정입니다.</li>
                                    </ul>
                                </div>
                            </section>
                        </main>
                    </>
                )
            })()}
        </>
    )
};